var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { checkSupport } from "./helpers/checkSupport.js";
import Factory from "./generated/emscripten/SDLWasmLib.mjs";
import { setupInteractivity } from "./setupInteractivity.js";
import { makeWorkerScript } from "./helpers/makeWorkerScript.js";
export const init = (_a) => __awaiter(void 0, [_a], void 0, function* ({ assetFolderPath, canvas, preinitializedWebGLContext, verbose = false, onAbort, }) {
    const checkSupportResult = checkSupport();
    // We could throw an exception here. However, then it's up to the client to decide if they
    // handle this properly or not. As this is not in fact an error, but an EXPECTED scenario
    // for ~1% of users according to current browser usage stats, I prefer to handle it with a
    // dedicated response type. This FORCES the consuming client code to think about it and
    // handle it.
    if (!checkSupportResult.all) {
        return {
            unsupportedReason: checkSupportResult,
        };
    }
    const versionedAssetFolderPath = typeof assetFolderPath === "string"
        ? assetFolderPath
        : assetFolderPath("15.2.1-beta.0"); // See inject-version.cjs
    const module = (yield Factory({
        canvas,
        locateFile: (path) => {
            // We can't use the URL constructor because we want to support relative paths too.
            return versionedAssetFolderPath + "/emscripten/" + path;
        },
        preinitializedWebGLContext,
        print: function (text) {
            if (verbose) {
                console.log(text);
            }
        },
        printErr: function (error) {
            if (verbose || (error && error.toLowerCase().includes("error"))) {
                // Only log actual errors, filter out Ogre initialization messages
                console.error(error);
            }
        },
        onAbort,
    }));
    // For now, we don't have any types for the module itself. Emscripten could generate these.
    // Here we link up Emscripten JS and Djinni TS with a simple cast.
    const application = module.Application;
    const vectorFontsBaseUrl = versionedAssetFolderPath + "/fonts";
    const workerEscriptenAssetFolderPath = (versionedAssetFolderPath === "."
        ? window.location.href // local asset hosting scenario (e.g. local dev)
        : versionedAssetFolderPath) + "/emscripten";
    // We create the worker via an object URL.
    // The alternative is to host the worker JS file somewhere, however browsers enforce a strict
    // same-origin policy when loading worker scripts. That means, if the page loading the worker
    // is strava.com/foo, the worker would need to be hosted at strava.com/something. This would
    // cause huge amounts of hassle for serving assets which can be used in production as well as
    // other environments. Remember: foo.strava.com and bar.strava.com are not the same origin.
    // Fortunately, imports which happen WITHIN the worker are allowed to be cross-origin, as long
    // as the usual fetch CORS conditions are met (Access-Control-Allow-Origin etc.) Phew!!!
    const blob = new Blob([makeWorkerScript(workerEscriptenAssetFolderPath)], {
        type: "application/javascript",
    });
    const workerUrl = URL.createObjectURL(blob);
    const terrainEngine = module.Application.create(vectorFontsBaseUrl, workerUrl);
    const { cleanupInteractivity } = setupInteractivity(canvas, application);
    const cleanup = () => {
        cleanupInteractivity();
        application.destroy();
    };
    return {
        terrainEngine,
        destroy: cleanup,
        versionInfo: application.getVersionInfo(),
    };
});
// Export all of the generated types and enums so they are available as top-level imports.
// TODO: maybe we don't need to expose all of these to the client?
export * from "./generated/types/activity_content.js";
export * from "./generated/types/application.js";
export * from "./generated/types/breadcrumb_recorder.js";
export * from "./generated/types/camera.js";
export * from "./generated/types/constants.js";
export * from "./generated/types/content.js";
export * from "./generated/types/debug_api.js";
export * from "./generated/types/engine_configuration.js";
export * from "./generated/types/gesture_recognizer_contol.js";
export * from "./generated/types/mre_holder.js";
export * from "./generated/types/offline.js";
export * from "./generated/types/osm_filter.js";
export * from "./generated/types/private_api.js";
export * from "./generated/types/route_content.js";
export * from "./generated/types/segments_filter.js";
export * from "./generated/types/strava_poi_filter.js";
export * from "./generated/types/structures.js";
export * from "./generated/types/terrain_engine.js";
export * from "./generated/types/utils.js";
export * from "./generated/types/window_lifecycle_listener.js";
